import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Navbar, CustomButton, LoadingSpinner, CustomModal } from '../components';
import { avatarsAPI, plansAPI, usersAPI } from '../services/api';
import './AvatarDetailsPage.css';

const AvatarDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [avatar, setAvatar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tokenBalance, setTokenBalance] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [addons, setAddons] = useState([]);
  const [loadingAddons, setLoadingAddons] = useState(false);
  const [avatarPlans, setAvatarPlans] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: ''
  });
  const [saving, setSaving] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [updatingAutoRenew, setUpdatingAutoRenew] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      toast.info('Please sign in to view avatar details');
      navigate('/signin');
    } else {
      setIsAuthenticated(true);
      fetchAvatarDetails();
      fetchTokenBalance();
    }
  }, [id, navigate]);

  const fetchAvatarDetails = async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 2000; // 2 seconds

    try {
      setLoading(true);

      // Fetch avatar details (only call needed - user data is not used on this page)
      const avatarResponse = await avatarsAPI.getAvatar(id);
      if (avatarResponse.status && avatarResponse.data) {
        setAvatar(avatarResponse.data);
        setEditFormData({
          first_name: avatarResponse.data.first_name || '',
          last_name: avatarResponse.data.last_name || ''
        });
        setLoading(false);
      } else {
        // Avatar not found - might still be processing
        if (retryCount < MAX_RETRIES) {
          // Retry if avatar might still be processing
          const delay = RETRY_DELAY * (retryCount + 1);
          console.log(`Avatar not found, retrying in ${delay}ms... (Attempt ${retryCount + 1}/${MAX_RETRIES})`);
          setTimeout(() => {
            fetchAvatarDetails(retryCount + 1);
          }, delay);
          return;
        } else {
          // Max retries reached
          toast.error('Avatar not found. It may still be processing. Please try again in a moment.');
          navigate('/chat');
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Error fetching avatar details:', error);

      // Check if it's a server error and retry
      const errorStatus = error?.status || error?.statusCode;
      const isServerError = errorStatus === 503 || errorStatus === 502 || errorStatus === 504;
      const isNotFound = errorStatus === 404;

      if ((isServerError || isNotFound) && retryCount < MAX_RETRIES) {
        // Retry with exponential backoff
        const delay = RETRY_DELAY * (retryCount + 1);
        console.log(`Server error (${errorStatus}), retrying in ${delay}ms... (Attempt ${retryCount + 1}/${MAX_RETRIES})`);
        toast.info(`Avatar is still processing. Retrying... (${retryCount + 1}/${MAX_RETRIES})`, {
          toastId: 'avatar-processing-retry',
          autoClose: delay
        });
        setTimeout(() => {
          fetchAvatarDetails(retryCount + 1);
        }, delay);
        return;
      }

      // Max retries reached or non-retryable error
      if (retryCount >= MAX_RETRIES) {
        toast.error('Avatar is still being processed. Please wait a moment and refresh the page.', {
          toastId: 'avatar-processing-error',
          autoClose: 5000
        });
      } else {
        toast.error('Failed to load avatar details. Please try again.', {
          toastId: 'avatar-details-error',
          autoClose: 5000
        });
      }
      navigate('/chat');
      setLoading(false);
    }
  };

  const fetchTokenBalance = async () => {
    try {
      const response = await plansAPI.getTokenBalance();
      if (response && (response.data || response.token_balance || response.usage_percentage !== undefined)) {
        setTokenBalance(response.data || response);
      }
    } catch (error) {
      console.warn('Failed to fetch token balance:', error);
    }
  };

  const fetchAvatarPlans = async () => {
    try {
      console.log('Fetching avatar plans for renewal...');
      let response;
      try {
        response = await plansAPI.getAvatarPlans();
      } catch (checkoutErr) {
        console.warn('GET /api/avatar-plans failed, falling back:', checkoutErr);
        response = await plansAPI.getAvatarPlans();
      }

      let avatarTypes = [];
      if (response && response.data && response.data.avatar_types && Array.isArray(response.data.avatar_types)) {
        avatarTypes = response.data.avatar_types;
      } else if (response && response.data && Array.isArray(response.data)) {
        avatarTypes = response.data;
      } else if (Array.isArray(response)) {
        avatarTypes = response;
      }

      const lovedOne = avatarTypes.find(a => (a.id === 2) || (a.name && String(a.name).toLowerCase().includes('loved')));
      if (lovedOne && (!lovedOne.plans || lovedOne.plans.length === 0)) {
        lovedOne.plans = [
          { id: 2, avatar_type_id: 2, monthly_price_usd: 19.99, yearly_price_usd: null, plan_days: 30 },
          { id: 3, avatar_type_id: 2, monthly_price_usd: null, yearly_price_usd: 150, plan_days: 365 }
        ];
      }

      if (avatarTypes.length > 0) {
        setAvatarPlans(avatarTypes);
      }
    } catch (error) {
      console.error('Error fetching avatar plans:', error);
      toast.error('Failed to load plan details.');
    }
  };

  const fetchAddons = async () => {
    setLoadingAddons(true);
    try {
      const response = await plansAPI.getAddons();

      let extractedAddons = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          extractedAddons = response.data;
        } else if (response.data.items && Array.isArray(response.data.items)) {
          extractedAddons = response.data.items;
        } else if (response.data.addons && Array.isArray(response.data.addons)) {
          extractedAddons = response.data.addons;
        }

        if (response.data.avatar_plans && Array.isArray(response.data.avatar_plans)) {
          const rawAvatarPlans = response.data.avatar_plans;
          const plansByType = {};
          rawAvatarPlans.forEach(plan => {
            const typeName = plan.avatar_type_name || 'Generic';
            if (!plansByType[typeName]) {
              plansByType[typeName] = { id: plan.avatar_type_id || Math.random(), name: typeName, plans: [] };
            }
            const normalizedPlan = { ...plan };
            const planInterval = (plan.interval || '').toLowerCase();
            const planPrice = Number(plan.price_usd || plan.price || 0);

            if (planInterval === 'yearly' || planInterval === 'year') {
              normalizedPlan.yearly_price_usd = planPrice;
            } else {
              normalizedPlan.monthly_price_usd = planPrice;
            }
            plansByType[typeName].plans.push(normalizedPlan);
          });
          setAvatarPlans(Object.values(plansByType));
        }
      } else if (Array.isArray(response)) {
        extractedAddons = response;
      }

      setAddons(extractedAddons);
    } catch (error) {
      console.error('Error fetching addons:', error);
      toast.error('Failed to load addons');
    } finally {
      setLoadingAddons(false);
    }
  };

  const handleAddonPayment = async (addon) => {
    try {
      if (!addon || !addon.id) {
        toast.error('Invalid upgrade plan selected');
        return;
      }

      localStorage.setItem('pending_addon_plan_id', addon.id);

      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/payment/addon/success`;
      const currentUrl = window.location.href.split('?')[0];

      const response = await plansAPI.createAddonPayPalOrder({
        addon_plan_id: addon.id,
        return_url: successUrl,
        cancel_url: currentUrl
      });
      console.log('PayPal create order response:', response);

      let approveUrl = null;

      if (response?.approve_url) {
        approveUrl = response.approve_url;
      } else if (response?.data?.approve_url) {
        approveUrl = response.data.approve_url;
      } else if (response?.links && Array.isArray(response.links)) {
        const link = response.links.find(l => l.rel === 'approve');
        if (link) approveUrl = link.href;
      } else if (response?.data?.links && Array.isArray(response.data.links)) {
        const link = response.data.links.find(l => l.rel === 'approve');
        if (link) approveUrl = link.href;
      }

      if (approveUrl) {
        window.location.href = approveUrl;
      } else {
        const msg = (typeof response?.msg === 'string' ? response.msg : null) ||
          (typeof response?.message === 'string' ? response.message : null) ||
          'No approval URL found';
        toast.error(`Failed to initiate payment: ${msg}`);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || error.msg || 'Payment initialization failed');
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset form data
      setEditFormData({
        first_name: avatar.first_name || '',
        last_name: avatar.last_name || ''
      });
    }
    setIsEditing(!isEditing);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveAvatar = async () => {
    if (!editFormData.first_name.trim() || !editFormData.last_name.trim()) {
      toast.error('First name and last name are required');
      return;
    }

    setSaving(true);
    try {
      // 1. Update Avatar Profile
      const response = await avatarsAPI.updateAvatarProfile(id, {
        first_name: editFormData.first_name,
        last_name: editFormData.last_name
      });

      if (response || response.status) {
        toast.success('Avatar updated successfully');
        setIsEditing(false);
        fetchAvatarDetails(); // Refresh data to show updated time
      }
    } catch (error) {
      console.error('Error updating avatar:', error);
      toast.error('Failed to update avatar');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadBackup = async () => {
    setDownloadingBackup(true);
    try {
      const blob = await usersAPI.exportBackup();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sayagain_backup.zip';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Backup download started');
    } catch (error) {
      console.error('Backup download failed:', error);
      toast.error(error.message || 'Failed to download backup');
    } finally {
      setDownloadingBackup(false);
    }
  };

  const handleCancelSubscription = async () => {
    setShowCancelConfirmModal(false);
    setCancellingSubscription(true);
    try {
      await avatarsAPI.cancelSubscription();
      toast.success('Subscription cancelled successfully');
      fetchAvatarDetails();
    } catch (error) {
      console.error('Cancel subscription failed:', error);
      toast.error(error.message || 'Failed to cancel subscription');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleToggleAutoRenew = async () => {
    const newValue = !avatar.auto_renew;
    setUpdatingAutoRenew(true);
    try {
      await avatarsAPI.updateAvatarProfile(id, { auto_renew: newValue });
      toast.success(`Auto-renew ${newValue ? 'enabled' : 'disabled'} successfully`);
      fetchAvatarDetails();
    } catch (error) {
      console.error('Update auto-renew failed:', error);
      toast.error(error.message || 'Failed to update auto-renew');
    } finally {
      setUpdatingAutoRenew(false);
    }
  };

  const handleSubscriptionPayment = async (plan, interval) => {
    try {
      if (!plan || !plan.id) {
        toast.error('Invalid plan selected');
        return;
      }

      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/payment/success`;
      const currentUrl = window.location.href.split('?')[0];

      // Assuming we need to use a payment creation API similar to addons but for subscriptions
      // Using existing paymentsAPI.createPaypalOrder based on api.js inspection
      const response = await import('../services/api').then(module =>
        module.paymentsAPI.createPaypalOrder({
          avatar_plan_id: plan.id,
          plan_interval: interval,
          return_url: successUrl,
          cancel_url: currentUrl
        })
      );

      console.log('PayPal create order response:', response);

      let approveUrl = null;

      if (response?.approve_url) {
        approveUrl = response.approve_url;
      } else if (response?.data?.approve_url) {
        approveUrl = response.data.approve_url;
      } else if (response?.links && Array.isArray(response.links)) {
        const link = response.links.find(l => l.rel === 'approve');
        if (link) approveUrl = link.href;
      } else if (response?.data?.links && Array.isArray(response.data.links)) {
        const link = response.data.links.find(l => l.rel === 'approve');
        if (link) approveUrl = link.href;
      }

      if (approveUrl) {
        let orderId = response?.order_id || response?.data?.order_id;
        if (!orderId) {
          try {
            orderId = new URL(approveUrl).searchParams.get('token');
          } catch (e) { }
        }

        localStorage.setItem('paypal_checkout_context', JSON.stringify({
          order_id: orderId,
          avatar_plan_id: plan.id,
          plan_interval: interval,
          auto_renew: true,
          started_from: `/avatar/${id}`
        }));

        window.location.href = approveUrl;
      } else {
        const msg = (typeof response?.msg === 'string' ? response.msg : null) ||
          (typeof response?.message === 'string' ? response.message : null) ||
          'No approval URL found';
        console.error('No approval URL found in PayPal response:', response);
        toast.error(`Failed to initiate payment: ${msg}`);
      }
    } catch (error) {
      console.error('Payment error:', error);
      const msg = error.message || error.msg || 'Payment initialization failed';
      toast.error(msg);
    }
  };

  const getAvatarInitials = () => {
    if (!avatar) return 'A';
    const firstName = avatar.first_name || '';
    const lastName = avatar.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'A';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelation = (relation) => {
    if (!relation) return 'N/A';
    // Capitalize first letter and handle special cases
    const relationMap = {
      'self': 'Self',
      'father': 'Father',
      'mother': 'Mother',
      'spouse': 'Spouse',
      'child': 'Child',
      'wife': 'Wife',
      'husband': 'Husband',
      'son': 'Son',
      'daughter': 'Daughter',
      'granddaughter': 'Granddaughter',
      'grandfather': 'Grandfather',
      'cousin': 'Cousin',
      'uncle': 'Uncle',
      'niece': 'Niece',
      'aunt': 'Aunt',
      'other': 'Other'
    };
    return relationMap[relation.toLowerCase()] || relation.charAt(0).toUpperCase() + relation.slice(1);
  };

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <div className="avatar-details-page">
        <Navbar />
        <LoadingSpinner fullScreen text="Loading avatar details..." />
      </div>
    );
  }

  if (!avatar) {
    return (
      <div className="avatar-details-page">
        <Navbar />
        <Container className="avatar-details-container">
          <div className="avatar-details-error">
            <h2>Avatar not found</h2>
            <p>The avatar you're looking for doesn't exist or you don't have access to it.</p>
            <CustomButton variant="primary" onClick={() => navigate('/chat')}>
              Back to Dashboard
            </CustomButton>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="avatar-details-page">
      <Navbar />

      <Container className="avatar-details-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Link to="/chat" className="back-link" style={{ marginBottom: 0 }}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </Link>
        </div>

        <div className="avatar-details-content">
          <Row>
            {/* Left Column: Profile & Usage Details */}
            <Col lg={4}>
              {/* Profile Card */}
              <div className="avatar-details-card" style={{ marginBottom: '20px' }}>
                <div className="avatar-details-avatar-circle gradient-sage">
                  {avatar.profile_image_url ? (
                    <img src={avatar.profile_image_url} alt="Avatar Profile" className="avatar-profile-image" />
                  ) : (
                    getAvatarInitials()
                  )}
                </div>
                <h2>{`${avatar.first_name || ''} ${avatar.last_name || ''}`.trim() || 'Avatar'}</h2>
                {avatar.status === 'active' && (
                  <div className="avatar-details-status">
                    <span className="status-badge status-active">
                      ● Active
                    </span>
                  </div>
                )}
              </div>

              {/* Usage Details Card */}
              <div className="avatar-details-info-card usage-details-card" style={{ padding: '24px' }}>
                <h3 style={{ margin: '0 0 15px 0', fontSize: '20px' }}>Usage Details</h3>
                <div className="details-grid-featured" style={{ gridTemplateColumns: '1fr', gap: '15px' }}>
                  <div className="detail-item">
                    <div className="detail-label" style={{ fontSize: '11px' }}>Messages Used</div>
                    <div className="detail-value" style={{ fontSize: '15px', fontWeight: '600', color: 'var(--soft-teal)' }}>
                      {(() => {
                        if (tokenBalance && tokenBalance.usage_percentage !== undefined) {
                          return `${tokenBalance.usage_percentage}% of Limit`;
                        }
                        const messagesUsed = avatar.messages_used || avatar.messages_count || 0;
                        const messagesLimit = avatar.messages_limit || avatar.total_messages || 500;
                        const percentage = messagesLimit > 0 ? Math.min(100, (messagesUsed / messagesLimit) * 100).toFixed(1) : 0;
                        return `${percentage}% of Limit`;
                      })()}
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label" style={{ fontSize: '11px' }}>Storage Used</div>
                    <div className="detail-value" style={{ fontSize: '15px', fontWeight: '600', color: 'var(--soft-teal)' }}>
                      {(() => {
                        const getStorageData = (obj) => {
                          if (!obj) return null;
                          if (obj.used_storage_mb !== undefined) return obj;
                          if (obj.storage && obj.storage.used_storage_mb !== undefined) return obj.storage;
                          return null;
                        };
                        const storageData = getStorageData(avatar);
                        if (storageData) {
                          const usedMb = parseFloat(storageData.used_storage_mb || 0);
                          let percentage = parseFloat(storageData.used_storage_percentage || 0);
                          return `${usedMb.toFixed(1)} MB / ${percentage.toFixed(1)}%`;
                        }
                        const mbUsed = parseFloat(avatar?.storage_used_mb || 0);
                        return `${mbUsed.toFixed(1)} MB Used`;
                      })()}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: '15px', padding: '12px', background: 'rgba(52, 211, 153, 0.03)', borderRadius: '12px', border: '1px dashed var(--border-subtle)' }}>
                  <p style={{ color: 'var(--slate)', fontSize: '12px', textAlign: 'center', margin: 0, fontStyle: 'italic' }}>
                    Usage data is updated in real-time. Upgrade plan to increase these limits.
                  </p>
                </div>
              </div>
            </Col>

            {/* Right Column: Information & Plan Details */}
            <Col lg={8}>
              {/* Information Card */}
              <div className="avatar-details-info-card" style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0 }}>Information</h3>
                  {!isEditing ? (
                    <button
                      onClick={handleEditToggle}
                      className="icon-btn"
                      title="Edit Details"
                      style={{ color: 'var(--soft-teal)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={handleEditToggle}
                        className="text-btn"
                        style={{ color: 'var(--slate)', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer' }}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveAvatar}
                        className="text-btn"
                        style={{ color: 'var(--soft-teal)', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer' }}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>
                <div className="details-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                  <div className="detail-item">
                    <div className="detail-label">First Name</div>
                    <div className="detail-value">
                      {isEditing ? (
                        <Form.Control
                          type="text"
                          name="first_name"
                          value={editFormData.first_name}
                          onChange={handleInputChange}
                          className="edit-input"
                        />
                      ) : (
                        avatar.first_name || 'N/A'
                      )}
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Last Name</div>
                    <div className="detail-value">
                      {isEditing ? (
                        <Form.Control
                          type="text"
                          name="last_name"
                          value={editFormData.last_name}
                          onChange={handleInputChange}
                          className="edit-input"
                        />
                      ) : (
                        avatar.last_name || 'N/A'
                      )}
                    </div>
                  </div>
                  {avatar.relation && (
                    <div className="detail-item">
                      <div className="detail-label">Relation</div>
                      <div className="detail-value">{formatRelation(avatar.relation)}</div>
                    </div>
                  )}
                  <div className="detail-item">
                    <div className="detail-label">Created At</div>
                    <div className="detail-value">{formatDate(avatar.created_at)}</div>
                  </div>
                </div>

                <div style={{ marginTop: '20px', borderTop: '1px solid var(--border-subtle)', paddingTop: '20px' }}>
                  <CustomButton
                    variant="secondary"
                    onClick={handleDownloadBackup}
                    disabled={downloadingBackup}
                    className="w-100"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      backgroundColor: '#f8fafc',
                      borderColor: '#e2e8f0',
                      color: '#475569'
                    }}
                  >
                    {downloadingBackup ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                    )}
                    {downloadingBackup ? 'Preparing Backup...' : 'Download Account Backup'}
                  </CustomButton>
                </div>
              </div>

              {/* Plan Details Card */}
              {(() => {
                const isLovedOne = avatar.avatar_type_id === 2 ||
                  (avatar.avatar_type && avatar.avatar_type.toLowerCase().includes('loved'));
                const subscription = avatar?.user?.subscription;
                const planDetails = subscription?.plan;
                const avatarPlan = avatar?.avatar_plan;

                if (isLovedOne || planDetails) {
                  const currentPlan = avatarPlan?.plan_interval || avatar.plan_interval || 'yearly';
                  const planPrice = planDetails?.price_usd !== undefined ? `$${planDetails.price_usd}` : (currentPlan === 'yearly' ? '$149/year' : '$19.99/month');
                  const planName = planDetails?.name || (isLovedOne ? 'For a loved one' : 'Standard Plan');

                  return (
                    <div className="avatar-details-info-card plan-details-card" style={{ padding: '24px' }}>
                      <h3 style={{ margin: '0 0 15px 0' }}>Plan Details</h3>
                      <div className="details-grid-featured" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
                        <div className="detail-item featured">
                          <div className="detail-label">Plan Type</div>
                          <div className="detail-value" style={{ textTransform: 'capitalize' }}>{planName}</div>
                        </div>
                        <div className="detail-item">
                          <div className="detail-label">Billing Period</div>
                          <div className="detail-value" style={{ textTransform: 'capitalize' }}>{currentPlan}</div>
                        </div>
                        <div className="detail-item">
                          <div className="detail-label">Price</div>
                          <div className="detail-value">{planPrice}</div>
                        </div>
                      </div>
                      <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                        <CustomButton
                          variant="primary"
                          onClick={() => { setShowSubscriptionModal(true); fetchAddons(); fetchAvatarPlans(); }}
                          className="flex-grow-1"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                        >
                          Upgrade
                        </CustomButton>

                        {avatar.auto_renew !== undefined && (
                          <CustomButton
                            variant="secondary"
                            onClick={handleToggleAutoRenew}
                            disabled={updatingAutoRenew}
                            className="flex-grow-1"
                            style={{ backgroundColor: '#f8fafc', borderColor: '#e2e8f0', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                          >
                            {updatingAutoRenew ? <Spinner animation="border" size="sm" /> : (avatar.auto_renew ? 'Disable' : 'Enable')}
                          </CustomButton>
                        )}

                        <CustomButton
                          variant="danger"
                          onClick={() => setShowCancelConfirmModal(true)}
                          disabled={cancellingSubscription}
                          className="flex-grow-1"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                        >
                          {cancellingSubscription ? <Spinner animation="border" size="sm" /> : 'Cancel'}
                        </CustomButton>
                      </div>
                    </div>
                  );
                } else if (!isLovedOne) {
                  return (
                    <div className="avatar-details-info-card" style={{ padding: '24px' }}>
                      <h3 style={{ margin: '0 0 15px 0' }}>Plan Details</h3>
                      <div className="details-grid">
                        <div className="detail-item">
                          <div className="detail-label">Plan Type</div>
                          <div className="detail-value">Free for 14 days</div>
                        </div>
                      </div>
                      <div style={{ marginTop: '20px' }}>
                        <CustomButton variant="primary" onClick={() => { setShowSubscriptionModal(true); fetchAddons(); fetchAvatarPlans(); }} className="w-100">
                          Upgrade Plan
                        </CustomButton>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </Col>
          </Row>
        </div>
      </Container>

      {/* Cancel Confirmation Modal */}
      <CustomModal
        show={showCancelConfirmModal}
        onHide={() => setShowCancelConfirmModal(false)}
        title="Cancel Subscription"
        subtitle="Are you sure you want to cancel your subscription?"
        iconVariant="danger"
        size="md"
        icon={
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        }
      >
        <div style={{ textAlign: 'center', padding: '10px 0' }}>
          <p style={{ color: 'var(--slate)', marginBottom: '25px' }}>
            This will stop future billing but you will keep access until the end of the current period.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
            <CustomButton variant="secondary" onClick={() => setShowCancelConfirmModal(false)} style={{ minWidth: '120px' }}>Cancel</CustomButton>
            <CustomButton variant="danger" onClick={handleCancelSubscription} loading={cancellingSubscription} style={{ minWidth: '120px' }}>Proceed</CustomButton>
          </div>
        </div>
      </CustomModal>

      {/* Upgrade Plan Modal */}
      <CustomModal
        show={showSubscriptionModal}
        onHide={() => setShowSubscriptionModal(false)}
        title="Upgrade Plan"
        subtitle="Unlock premium features with our Pro plans"
        iconVariant="gold"
        size="lg"
        icon={
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        }
      >
        <div style={{ padding: '0 0 20px 0', maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
          <div style={{ marginBottom: '30px', padding: '0 15px' }}>
            <h4 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', color: 'var(--soft-black)', textAlign: 'center' }}>Avatar Subscriptions</h4>
            <div className="addons-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
              {avatarPlans && avatarPlans.length > 0 ? (
                avatarPlans.map(type => {
                  const plansList = type.plans || [];
                  return plansList.map(plan => {
                    const isYearly = Number(plan.yearly_price_usd) > 0;
                    const priceText = isYearly ? `$${plan.yearly_price_usd}/year` : `$${plan.monthly_price_usd}/month`;
                    const billingPeriod = isYearly ? 'yearly' : 'monthly';
                    return (
                      <div key={`avatar-plan-${plan.id}`} style={{ background: 'white', border: '1px solid var(--border-subtle)', borderRadius: '16px', padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <div style={{ marginBottom: '16px' }}>
                          <h5 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600', color: 'var(--soft-black)' }}>{type.name || 'Avatar Plan'} - {isYearly ? 'Yearly' : 'Monthly'}</h5>
                          <div style={{ marginTop: '5px', fontSize: '14px', fontWeight: 'bold', color: 'var(--soft-teal)' }}>{priceText}</div>
                        </div>
                        <button onClick={() => { setShowSubscriptionModal(false); handleSubscriptionPayment(plan, billingPeriod); }} style={{ background: 'var(--soft-teal)', color: 'white', border: 'none', padding: '10px 32px', borderRadius: '24px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>Buy</button>
                      </div>
                    );
                  });
                })
              ) : (
                <div style={{ textAlign: 'center', padding: '10px', color: 'var(--slate)' }}>Loading subscriptions...</div>
              )}
            </div>
          </div>

          <hr style={{ borderColor: 'var(--border-subtle)', margin: '20px 0' }} />

          <div style={{ padding: '0 15px' }}>
            <h4 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', color: 'var(--soft-black)', textAlign: 'center' }}>Add-on Plans</h4>
            {loadingAddons ? (
              <div style={{ textAlign: 'center', padding: '20px' }}><Spinner animation="border" size="sm" /></div>
            ) : addons.length > 0 ? (
              <div className="addons-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                {addons.map((addon) => (
                  <div key={addon.id} style={{ background: 'white', border: '1px solid var(--border-subtle)', borderRadius: '16px', padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                    <div style={{ marginBottom: '16px' }}>
                      <h5 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600', color: 'var(--soft-black)' }}>{addon.name}</h5>
                      <p style={{ margin: 0, fontSize: '13px', color: 'var(--slate)' }}>{addon.description}</p>
                      <div style={{ marginTop: '5px', fontSize: '14px', fontWeight: 'bold', color: 'var(--soft-teal)' }}>${addon.price_usd}</div>
                    </div>
                    <button onClick={() => handleAddonPayment(addon)} style={{ background: 'var(--soft-teal)', color: 'white', border: 'none', padding: '10px 32px', borderRadius: '24px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>Buy</button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: 'var(--slate)' }}>No addons available.</div>
            )}
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default AvatarDetailsPage;
